import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";

const BACKEND_URL = "";

// Public owner view — accessed via magic-link in email, no login required
const OwnerTriggerView = () => {
  const { token } = useParams();
  const [trigger, setTrigger] = useState(null);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = async () => {
    try {
      const r = await fetch(`${BACKEND_URL}/api/trigger/${token}`);
      if (r.ok) {
        setTrigger(await r.json());
      } else {
        setError("This link is invalid or has expired.");
      }
    } catch {
      setError("Couldn't load the trigger. Please try again.");
    }
    setLoading(false);
  };
  useEffect(() => {
    load();
    const i = setInterval(load, 45000);
    return () => clearInterval(i);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Web Speech API mic for owner replies
  const [listening, setListening] = useState(false);
  const [rec, setRec] = useState(null);
  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;
    const r = new SR();
    r.continuous = true; r.interimResults = true; r.lang = "en-US";
    r.onresult = (e) => {
      let finalT = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        if (e.results[i].isFinal) finalT += e.results[i][0].transcript + " ";
      }
      if (finalT) setReply((prev) => (prev ? `${prev} ${finalT.trim()}` : finalT.trim()));
    };
    r.onerror = () => setListening(false);
    r.onend = () => setListening(false);
    setRec(r);
  }, []);
  const toggleMic = () => {
    if (!rec) return;
    if (listening) { try { rec.stop(); } catch {} setListening(false); }
    else { try { rec.start(); setListening(true); } catch {} }
  };

  const sendReply = async () => {
    if (!reply.trim()) return;
    setSending(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/trigger/${token}/reply`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text: reply }),
      });
      if (r.ok) {
        setTrigger(await r.json());
        setReply("");
      }
    } catch {}
    setSending(false);
  };

  if (loading) {
    return (
      <div style={{ minHeight: "100vh", background: "#030B1A", display: "flex", alignItems: "center", justifyContent: "center", color: "#7E92A8" }}>
        Loading…
      </div>
    );
  }
  if (error || !trigger) {
    return (
      <div style={{ minHeight: "100vh", background: "#030B1A", display: "flex", alignItems: "center", justifyContent: "center", color: "#FF4D6D", padding: 20, textAlign: "center" }}>
        <div>
          <h2 style={{ color: "#fff", margin: "0 0 10px" }}>Link Expired</h2>
          <p>{error || "This magic-link is no longer valid. Please contact Moovin Forward directly."}</p>
        </div>
      </div>
    );
  }

  const isResolved = trigger.status === "resolved";

  return (
    <div style={{ minHeight: "100vh", background: "#030B1A", padding: "20px 16px", fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif" }}>
      <div style={{ maxWidth: 640, margin: "0 auto" }}>
        {/* Header */}
        <div style={{ background: "linear-gradient(135deg, #0a1929 0%, #030B1A 100%)", borderRadius: 12, padding: "18px 20px", marginBottom: 16, border: "1px solid rgba(0,255,255,0.18)" }}>
          <div style={{ fontSize: 11, color: "#00FFFF", letterSpacing: "0.14em", fontWeight: 800, marginBottom: 6 }}>MOOVIN FORWARD · FIELD TRIGGER</div>
          <h1 style={{ color: "#fff", margin: "0 0 4px", fontSize: 19, lineHeight: 1.3 }}>{trigger.address}</h1>
          <p style={{ color: "#ADBED1", fontSize: 12, margin: 0 }}>Our crew flagged something on-site. Review the photo below and reply when you can — we're standing by.</p>
        </div>

        {/* Photo / Video */}
        <div style={{ borderRadius: 10, overflow: "hidden", marginBottom: 16, background: "#000" }}>
          {trigger.photo?.is_video ? (
            <video src={`${BACKEND_URL}/api/trigger/${token}/photo`} controls preload="metadata" style={{ width: "100%", maxHeight: 380 }} />
          ) : (
            <img src={`${BACKEND_URL}/api/trigger/${token}/photo`} alt="Trigger evidence" style={{ width: "100%", maxHeight: 380, objectFit: "contain", display: "block" }} />
          )}
        </div>

        {/* Status pill */}
        <div style={{ marginBottom: 14, display: "flex", gap: 8, flexWrap: "wrap" }}>
          <span style={{ padding: "5px 11px", borderRadius: 999, fontSize: 11, fontWeight: 800, letterSpacing: "0.04em", background: trigger.status === "open" ? "rgba(255,77,109,0.20)" : trigger.status === "responding" ? "rgba(255,179,71,0.20)" : "rgba(0,220,120,0.20)", color: trigger.status === "open" ? "#FF4D6D" : trigger.status === "responding" ? "#FFB347" : "#00DC78" }}>
            {trigger.status === "open" ? "🔴 OPEN — Crew Awaiting Response" : trigger.status === "responding" ? "🟡 MOOVIN FORWARD RESPONDING" : "🟢 RESOLVED"}
          </span>
        </div>

        {/* Messages */}
        <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 20 }} data-testid="owner-trigger-messages">
          {(trigger.messages || []).map((m) => {
            const isOwner = m.author_role === "owner";
            const roleMeta = {
              crew: { color: "#FFB347", label: "Crew" },
              ceo: { color: "#00FFFF", label: "Shannon (Moovin Forward CEO)" },
              coo: { color: "#A78BFA", label: "Shaneka (Moovin Forward COO)" },
              admin: { color: "#5AC8D8", label: "Moovin Forward Team" },
              owner: { color: "#00DC78", label: "You" },
            };
            const rm = roleMeta[m.author_role] || { color: "#ADBED1", label: m.author_role };
            return (
              <div key={m.msg_id} style={{ padding: 12, borderRadius: 10, background: isOwner ? "rgba(0,220,120,0.06)" : "rgba(255,255,255,0.03)", borderLeft: `3px solid ${rm.color}` }}>
                <div style={{ fontSize: 10, color: rm.color, fontWeight: 800, letterSpacing: "0.06em", marginBottom: 4 }}>
                  {rm.label.toUpperCase()} · {new Date(m.created_at).toLocaleString()}
                </div>
                <div style={{ color: "#fff", fontSize: 13, lineHeight: 1.55, whiteSpace: "pre-wrap" }}>{m.text}</div>
              </div>
            );
          })}
        </div>

        {/* Reply box */}
        {!isResolved && (
          <div style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(0,255,255,0.18)", borderRadius: 12, padding: 14 }}>
            <div style={{ fontSize: 12, color: "#00FFFF", fontWeight: 700, marginBottom: 8 }}>💬 Your reply</div>
            <div style={{ position: "relative" }}>
              <textarea
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                placeholder="Type or tap the mic to dictate your response..."
                rows={3}
                data-testid="owner-reply-textarea"
                style={{ width: "100%", padding: "10px 44px 10px 12px", borderRadius: 8, border: "1px solid rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.04)", color: "#fff", fontSize: 14, fontFamily: "inherit", resize: "vertical", boxSizing: "border-box" }}
              />
              {rec && (
                <button
                  type="button"
                  onClick={toggleMic}
                  data-testid="owner-reply-mic"
                  style={{ position: "absolute", right: 8, top: 8, width: 32, height: 32, borderRadius: "50%", border: "none", background: listening ? "#FF4D6D" : "rgba(0,255,255,0.18)", color: listening ? "#fff" : "#00FFFF", cursor: "pointer", fontSize: 14, animation: listening ? "mfPulse 1.2s infinite" : "none" }}
                  title={listening ? "Stop dictation" : "Start dictation"}
                >
                  {listening ? "■" : "🎤"}
                </button>
              )}
            </div>
            <button
              onClick={sendReply}
              disabled={!reply.trim() || sending}
              data-testid="owner-reply-send"
              style={{ marginTop: 10, width: "100%", padding: "12px 14px", borderRadius: 8, border: "1px solid #00DC78", background: "rgba(0,220,120,0.18)", color: "#00DC78", fontSize: 14, fontWeight: 800, cursor: sending || !reply.trim() ? "not-allowed" : "pointer", opacity: sending || !reply.trim() ? 0.5 : 1 }}
            >
              {sending ? "Sending…" : "Send Reply"}
            </button>
          </div>
        )}

        <p style={{ color: "#7E92A8", fontSize: 11, textAlign: "center", marginTop: 24, lineHeight: 1.5 }}>
          Moovin Forward LLC · No Brokerage. No Property Management. No Leasing.<br/>
          Secured magic-link · timestamped for your records.
        </p>
      </div>
    </div>
  );
};

export default OwnerTriggerView;
